/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { ItemListViewItem } from "@coworker/apprestructured/src/media-maintenance/components/ItemListViewItem/ItemListViewItem";
import { LocalCombinationItemsListView } from "@coworker/apprestructured/src/media-maintenance/components/LocalCombinations/LocalCombinationItemsListView/LocalCombinationItemsListView";
import { useMediaItemsWithTasks } from "@coworker/apprestructured/src/media-maintenance/components/MediaTasksSection/hooks/useMediaItemsWithTasks";
import { MediaItemType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemType";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import { FilterPanelMobile } from "@coworker/apprestructured/src/shared/filters/components/FilterPanelMobile";
import { useItemsInfoBatch } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath";
import { MEDIA_TASK_TYPE_NAMES } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { Trans, useTranslation } from "@coworker/locales";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import styled from "styled-components";
import { sortArticlesByItemName } from "../../../helpers/sorters";
import useFlag, { FLAGS } from "../../../hooks/useFlag";
import { useInputPopup } from "../../InputPopup";
import { PleaseFillIn } from "../../Reusable/PleaseFillIn";
import { FullWidthButton } from "../../SkapaButton";
import {
  isCombinationParentArticle,
  processArticles,
  selectedArticleIdsToArticles,
} from "../Helpers/articleHelpers";
import { QueryKeys } from "../hooks/queryKeys";
import {
  AggregatedArticle,
  aggregateArticles,
} from "../hooks/useAggregatedArticles";
import { useArticlesByGroupId } from "../hooks/useArticlesByGroupId";
import { useCombinedArticles } from "../hooks/useCombinedArticles";
import { useEDSData } from "../hooks/useEDSData";
import { useGroupsByMediaId } from "../hooks/useGroupsByMediaId";
import { useGroupMediaArticlesFilters } from "../hooks/useMediaArticlesFilters";
import { useMediaById } from "../hooks/useMediaById";
import { useSalesStopData } from "../hooks/useSalesStopData";
import { useTasksByMedia } from "../hooks/useTasksByMedia";
import {
  hasOngoingTask,
  selectArticleIdNbrArticles,
} from "../Molecules/MediaArticles.helper";
import { MediaImagesCarousel } from "../Molecules/MediaImagesCarousel";
import { SearchBar } from "../Molecules/SearchBar";
import { SelectArticles } from "../Molecules/SelectArticles";
import { DeleteGroupModal } from "../Organisms/Modals/DeleteGroupModal";
import { EditGroupModal } from "../Organisms/Modals/EditGroupModal";
import { GroupActionsModal } from "../Organisms/Modals/GroupActionModal";
import { MediaArticlesModals } from "../Organisms/Modals/MediaArticlesModals";
import { ArticleViewSkeleton } from "../Skeletons/ArticleViewSkeleton";
import {
  ArticleListItemMobile,
  CenterContainer,
  CenterContainerWithGap,
  ListWrapper,
  SizedText,
} from "../styles/styles";
import { joinProductArticleIds } from "../tools";
import { ArticleIdNbrArticles, Articles } from "../types/article";
import { Group } from "../types/groups";
import { MediaResponse } from "../types/media";
import { ModalTypes } from "../types/views";
import { useItemsStockInfo } from "./useItemsStockInfo";

const ArticleFiltersContainer = styled("div")`
  border-bottom: 1px solid var(--grey200);
`;
const SearchBarContainer = styled("div")`
  padding: 16px;
`;
const SelectButtonContainer = styled("div")`
  padding: 16px;
  text-align: center;
  position: sticky;
  top: 56px;
  background-color: white;
  z-index: 1;
`;

export function GroupView() {
  const { groupId } = useParams();
  const [queryParams] = useSearchParams();
  const { t } = useTranslation();
  const { getInput } = useInputPopup();
  const queryClient = useQueryClient();
  const { showToast } = useToastNotification();
  const navigate = useFixaNavigate();

  const mediaId = queryParams.get("mediaId") ?? undefined;

  const { data: articles } = useArticlesByGroupId(groupId);
  const { data: groups } = useGroupsByMediaId(mediaId);
  const { data: mediaResponse } = useMediaById(mediaId);
  const articleNumbers = joinProductArticleIds(articles ?? []);
  const storeId = useStoreId();
  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, articleNumbers);
  const { data: salesStopData } = useSalesStopData(storeId, articleNumbers);
  const { taskLinks } = useTasksByMedia(mediaId);
  const { data: taskTypes = [] } = useAllTaskTypes();

  const enableMediaV2Tasks = useFlag(FLAGS.ENABLE_MEDIA_V2_TASKS);

  const mediaTaskTypeIds = useMemo(
    () =>
      taskTypes
        .filter((tt) =>
          (MEDIA_TASK_TYPE_NAMES as readonly string[]).includes(tt.name)
        )
        .map((tt) => tt.id),
    [taskTypes]
  );

  const [currentModalType, setCurrentModalType] = useState<ModalTypes>(
    ModalTypes.DEFAULT_VIEW
  );

  const [isAddingArticles, setIsAddingArticles] = useState(false);
  const [isSelectArticlesMode, setIsSelectArticlesMode] = useState(false);
  const [selectedArticleIdsNbrArticles, setSelectedArticleIdsNbrArticles] =
    useState<ArticleIdNbrArticles[]>([]);
  const [warningText, setWarningText] = useState("");

  const [selectedArticles, setSelectedArticles] = useState<Articles>([]);
  const [isSearchedResult, setIsSearchedResult] = useState(false);
  const [articleSearchResult, setArticleSearchResult] = useState<
    AggregatedArticle[]
  >([]);

  const allArticlesInMedia = useCombinedArticles(
    countryId,
    mediaResponse?.media.storeId ?? "",
    mediaId ?? "",
    articles
  );

  const { data: itemsStockInfo } = useItemsStockInfo(allArticlesInMedia);

  // Process articles to separate active and combination articles
  const { activeArticles, groupedCombinationArticles } = useMemo(() => {
    const { activeArticles, groupedCombinationArticles } = processArticles(
      allArticlesInMedia ?? []
    );
    const activeArticlesWithStockInfo = activeArticles.map((article) => ({
      ...article,
      stockInfo: itemsStockInfo?.[article.productArticleId],
    }));
    return {
      activeArticles: activeArticlesWithStockInfo,
      groupedCombinationArticles,
    };
  }, [allArticlesInMedia, itemsStockInfo]);

  const uniqueCombinationArticles = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(groupedCombinationArticles ?? {}).map(([key, arts]) => [
          key,
          aggregateArticles(arts),
        ])
      ),
    [groupedCombinationArticles]
  );

  const articlesToUse = isSearchedResult ? articleSearchResult : activeArticles;
  const {
    useFiltersFactory,
    filterValues,
    setFilterValues,
    useFilteredArticleData,
  } = useGroupMediaArticlesFilters(articlesToUse as AggregatedArticle[]);
  const articlesToShow =
    useFilteredArticleData(articlesToUse as AggregatedArticle[]) ?? [];

  // Fetch v2 tasks: array of { article_id, item_no } pairs
  const { data: v2TasksArray = [] } = useMediaItemsWithTasks(
    enableMediaV2Tasks ? mediaId : undefined,
    mediaTaskTypeIds
  );

  // Create lookup Maps for Group view (both article_id AND item_no must match)
  const v2TasksMap = useMemo(() => {
    const map = new Map<string, boolean>();
    v2TasksArray.forEach(({ article_id, item_no }) => {
      // Create composite key: articleId|itemNo
      const compositeKey = `${article_id}|${item_no}`;
      map.set(compositeKey, true);
    });
    return map;
  }, [v2TasksArray]);

  // Merge articles and sub-articles for searching, and derive parentArticle immutably.
  const searchableArticles = useMemo(() => {
    const merged = [
      ...Object.values(uniqueCombinationArticles).flatMap((x) => x),
      ...activeArticles,
    ];

    return merged.map((article) => {
      if (!article.parentArticleCombinationNumber) {
        return article;
      }

      const parentArticle = merged.find(
        (a) =>
          a.productArticleId === article.parentArticleCombinationNumber &&
          a.groupId === article.groupId
      );

      if (!parentArticle) {
        return article;
      }

      return {
        ...article,
        parentArticle,
      };
    });
  }, [uniqueCombinationArticles, activeArticles]);
  const group = groups?.find((group) => group.id === groupId);

  const previousPath = useMemo(
    () => `/mediamaintenance/tabs/${mediaResponse?.media.id}?tab=groups-tab`,
    [mediaResponse?.media.id]
  );

  const linkbar = useMemo(() => {
    if (
      !group?.name ||
      !mediaResponse?.media.id ||
      !mediaResponse?.media.name
    ) {
      return <LinkbarLoading />;
    }

    return (
      <Linkbar
        currentLabel={group.name}
        items={[
          {
            label: t("mediaMaintenanceString"),
            to: "/mediamaintenance",
          },
          {
            label: mediaResponse.media.name,
            to: previousPath,
          },
        ]}
      />
    );
  }, [
    group?.name,
    mediaResponse?.media.id,
    mediaResponse?.media.name,
    previousPath,
    t,
  ]);

  const itemNos = useMemo(
    () =>
      articles
        ?.filter(
          (article) => !isCombinationParentArticle(article.productArticleType)
        )
        .map((a) => a.productArticleId)
        .filter(Boolean),
    [articles]
  );
  const { data: itemsInfo, isLoading } = useItemsInfoBatch(itemNos);
  const itemInfoMap = useMemo(() => {
    const map = new Map<string, Item>();

    itemsInfo?.forEach((item) => {
      map.set(item.no, item);
    });

    return map;
  }, [itemsInfo]);
  const fullScreenPopup = (
    children: React.ReactNode,
    scrollRestoreKey?: string,
    scrollRestoreReady?: boolean
  ) => (
    <FullScreenPopup
      appBarConfig={{
        title: group?.name ?? t("loadingString"),
        actions: [
          {
            name: "settings",
            icon: <FixaSSRIcon paths={PencilIconPath} />,
            onClick: () => setCurrentModalType(ModalTypes.GROUP_ACTIONS),
            position: "right",
          },
        ],
      }}
      actionBarContent={
        <CenterContainerWithGap $centerText={true}>
          {isSelectArticlesMode ? (
            <>
              <PleaseFillIn
                show={!!warningText}
                alternateWarningTranslation={warningText}
                centerText={true}
              />
              <FullWidthButton
                text={t("continue2String")}
                onClick={async () => {
                  if (selectedArticleIdsNbrArticles.length > 0) {
                    setCurrentModalType(
                      ModalTypes.GROUP_SELECTED_ARTICLES_ACTIONS
                    );
                    setSelectedArticles(
                      selectedArticleIdsToArticles(
                        activeArticles,
                        selectedArticleIdsNbrArticles,
                        Object.values(groupedCombinationArticles).flat()
                      )
                    );
                  } else {
                    setWarningText(t("selectArticlesToContinueString"));
                  }
                }}
                type={"primary"}
              />
            </>
          ) : (
            <FullWidthButton
              type="primary"
              text={t("addArticlesToGroupString")}
              onClick={async () => {
                await getInput("addMultipleArticles", {
                  mediaId,
                  groupId,
                  setIsAddingArticles,
                });
                await queryClient.invalidateQueries({
                  queryKey: [QueryKeys.ArticlesByGroup, { id: groupId }],
                });
              }}
            />
          )}
        </CenterContainerWithGap>
      }
      noPadding
      fullHeightContent
      linkbar={linkbar}
      scrollRestoreKey={scrollRestoreKey}
      scrollRestoreReady={scrollRestoreReady}
    >
      <>
        {children}
        <GroupActionsModal
          modalVisibility={currentModalType === ModalTypes.GROUP_ACTIONS}
          onTriggerNewModal={(modalType: ModalTypes) => {
            setCurrentModalType(modalType);
          }}
          mediaId={mediaId ?? ""}
          currentGroup={group as Group}
          newGroupEditModal={true}
        />
        <EditGroupModal
          modalVisibility={currentModalType === ModalTypes.EDIT_GROUP_NAME}
          currentGroup={group as Group}
          onTriggerNewModal={(modalType: ModalTypes) => {
            setCurrentModalType(modalType);
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.GroupsByMedia, mediaId],
            });
          }}
          groups={groups || []}
        />
        <DeleteGroupModal
          modalVisibility={currentModalType === ModalTypes.DELETE_GROUP}
          mediaId={mediaId || ""}
          allArticles={articles || []}
          currentGroup={group}
          onTriggerNewModal={() => {
            navigate(previousPath, { replace: true });
          }}
        />
        {currentModalType !== ModalTypes.DEFAULT_VIEW && (
          <MediaArticlesModals
            selectedArticles={selectedArticles}
            allArticles={activeArticles || []}
            currentModalType={currentModalType}
            onModalTypeChange={(modalType: ModalTypes) => {
              if (modalType === ModalTypes.DEFAULT_VIEW) {
                setIsSelectArticlesMode(false);
                setSelectedArticles([]);
                setSelectedArticleIdsNbrArticles([]);
              }
              setWarningText("");
              setCurrentModalType(modalType);
            }}
            groups={groups || []}
            storeId={mediaResponse?.media.storeId ?? ""}
            mediaId={mediaResponse?.media.id ?? ""}
            currentGroup={group as Group}
            media={mediaResponse as MediaResponse}
          />
        )}
      </>
    </FullScreenPopup>
  );

  const articlesWasAdded = useRef(isAddingArticles);
  useEffect(() => {
    if (articlesWasAdded.current && !isAddingArticles) {
      showToast(t("articlesAddedSuccessfullyString"), undefined, undefined, 2);
    }
    articlesWasAdded.current = isAddingArticles;
  }, [isAddingArticles, showToast, t]);

  if (!articles || !groups) {
    return <>{fullScreenPopup(<ArticleViewSkeleton />)}</>;
  }

  if (isAddingArticles) {
    return (
      <FullScreenPopup
        noPadding
        linkbar={linkbar}
        appBarConfig={{
          title: group?.name ?? t("loadingString"),
          actions: [
            {
              name: "settings",
              icon: <FixaSSRIcon paths={PencilIconPath} />,
              onClick: () => {},
              position: "right",
            },
          ],
        }}
      >
        {[...Array(3)].map((_, index) => (
          <SimpleArticleCardSkeleton key={index} />
        ))}
      </FullScreenPopup>
    );
  }

  if (!articles || articles?.length === 0) {
    return (
      <>
        {fullScreenPopup(
          <CenterContainer $centerText={true} style={{ margin: "48px 0px" }}>
            <SizedText $fontSize={20} $bold={true}>
              <Trans>noArticlesInGroupString</Trans>
            </SizedText>
          </CenterContainer>
        )}
      </>
    );
  }

  return (
    <>
      {mediaResponse &&
        fullScreenPopup(
          <>
            <MediaImagesCarousel
              media={mediaResponse.media}
              groupId={group?.id}
            />
            <ArticleFiltersContainer>
              <FilterPanelMobile
                useFiltersFactory={useFiltersFactory}
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                feature="media-articles"
              />
            </ArticleFiltersContainer>
            <SearchBarContainer>
              <SearchBar
                articles={(searchableArticles as AggregatedArticle[]) ?? []}
                onSearchResults={(
                  isSearchResult: boolean,
                  result: AggregatedArticle[]
                ) => {
                  setIsSearchedResult(isSearchResult);
                  setArticleSearchResult(result);
                }}
              />
            </SearchBarContainer>
            <SelectButtonContainer>
              <FixaButton
                type={"secondary"}
                text={
                  isSelectArticlesMode
                    ? t("cancelString")
                    : t("selectArticlesString")
                }
                onClick={() => {
                  setIsSelectArticlesMode((prev) => !prev);
                  setSelectedArticleIdsNbrArticles([]);
                  setWarningText("");
                }}
                fluid
              />
            </SelectButtonContainer>
            <SelectArticles
              selectedArticles={selectedArticleIdsNbrArticles}
              uniqueArticles={activeArticles}
              filteredArticles={articlesToShow}
              isSelected={isSelectArticlesMode}
              onSelectAll={(selectAllChecked: boolean) => {
                if (selectAllChecked) {
                  setSelectedArticleIdsNbrArticles(
                    activeArticles?.map((article) => ({
                      articleId: article.id,
                      nbrArticles: article.nbrArticles,
                      productArticleId: article.productArticleId,
                    })) ?? []
                  );
                } else {
                  setSelectedArticleIdsNbrArticles([]);
                }
              }}
            />
            <ListWrapper>
              {articlesToShow.sort(sortArticlesByItemName).map((article) => {
                const hasV1Tasks = hasOngoingTask([article.id], taskLinks);
                // For Group view: BOTH article_id AND item_no must match
                const compositeKey = `${article.id}|${article.productArticleId}`;
                const hasV2Tasks = v2TasksMap.get(compositeKey) ?? false;
                const hasOngoingTasks = hasV1Tasks || hasV2Tasks;

                return (
                  <React.Fragment key={article.id}>
                    <ArticleListItemMobile>
                      <ItemListViewItem
                        article={article}
                        stockInfo={itemsStockInfo?.[article.productArticleId]}
                        itemCardRenderType={ItemCardRenderType.link}
                        media={mediaResponse?.media}
                        hasOngoingTasks={hasOngoingTasks}
                        edsData={
                          edsData?.find(
                            (eds: { productArticleId: string }) =>
                              eds.productArticleId === article.productArticleId
                          ) ?? null
                        }
                        salesStopData={
                          salesStopData?.find(
                            (salesStop) =>
                              salesStop.productArticleId ===
                              article.productArticleId
                          ) ?? null
                        }
                        selectArticleIdsMode={isSelectArticlesMode}
                        handleSelectedItems={(articleIdNbrArticles) =>
                          selectArticleIdNbrArticles(
                            articleIdNbrArticles,
                            selectedArticleIdsNbrArticles,
                            setSelectedArticleIdsNbrArticles
                          )
                        }
                        isSelected={
                          !!selectedArticleIdsNbrArticles.find(
                            (item) => item.articleId === article.id
                          )
                        }
                        itemInfo={
                          itemInfoMap.get(article.productArticleId) ?? null
                        }
                        itemInfoLoading={isLoading}
                        mediaItemType={
                          isSearchedResult
                            ? article.parentArticleCombinationNumber
                              ? MediaItemType.LC_SUBITEM
                              : MediaItemType.REGULAR_ITEM
                            : MediaItemType.REGULAR_ITEM
                        }
                      />
                    </ArticleListItemMobile>
                    {isCombinationParentArticle(article.productArticleType) && (
                      <LocalCombinationItemsListView
                        parentArticle={article}
                        itemsStockInfo={itemsStockInfo}
                        combinationArticles={groupedCombinationArticles}
                        edsData={edsData ?? null}
                        salesStopData={salesStopData}
                        mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </ListWrapper>
          </>,
          `groupView-scroll-${groupId}`,
          true
        )}
    </>
  );
}
